import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import { FormikHandlers } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AsyncSelectScroll from "../../../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../components/CustomSelect";
import Input from "../../../../../../../components/Input";
import MaskMultiSelectScroll from "../../../../../../../components/MaskMultiSelectScroll";
import { ScrollArea } from "../../../../../../../components/ScrollBar";
import Text from "../../../../../../../components/Text";
import ProviderSelectScroll from "../../../../../../../components/ProviderSelectScroll";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import useGetCataLogCode from "../../../../../../../queries/catalog/use-get-catalog-code";
import { useGetPharmacyByType } from "../../../../../../../queries/patient-chart/order/use-get-pharmacy-by-type";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../../../utils/utils";
import {
  PrescriptionForm,
  SUBSTITUTIONS_OR_DISPENSE_OPTIONS_LABELS,
  TYPE_OPTIONS,
} from "../../../../patient/patient-list/patient-chart/order/order-utils";
// import { NewMedicationDrawer } from "./NewPublicMedAddDrawer";
import NumberInput from "../../../../../../../components/NumberInput";
import DrugCatalogDrawer from "../../../../../../../components/app/admin/Library/catalog/DrugCatalogDrawer";
import { INPUT_FIELD_HEIGHT } from "../../../../../../../theme";

interface PrescriptionFieldsProps {
  values: PrescriptionForm;
  readOnly?: boolean;
  touched?: any;
  errors?: any;
  handleBlur: FormikHandlers["handleBlur"];
  handleChange: FormikHandlers["handleChange"];
  setFieldValue: any;
  icdCodes?: { value: string; label: string }[];
}

export const PrescriptionFields = ({
  values,
  handleBlur,
  touched,
  errors,
  handleChange,
  setFieldValue,
  readOnly,
  icdCodes,
}: PrescriptionFieldsProps) => {
  const { patientId } = useParams();
  const [pharmacyKeys, setPharmacyKeys] = useDebounce<string>("");
  const newMedDisc = useDisclosure();

  const [icdSearch, setIcdSearch] = useDebounce<string>("");
  const [icdPagination, setIcdPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: icdCodesRaw, isFetching: isIcdFetching } = useGetCataLogCode({
    pagination: icdPagination,
    searchKey: icdSearch,
    type: "ICD",
    active: undefined,
    archive: false,
  });

  const [pharmacyPagination, setPharmacyPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: allPharmacies, isFetching: isPharmacyFetching } =
    useGetPharmacyByType({
      pagination: pharmacyPagination,
      patientUuid: patientId ?? "",
      type: "PHARMACY",
      searchString: pharmacyKeys,
      xTENANTID: getXtenantId(),
    });

  const [medicationSearch, setMedicationSearch] = useDebounce<string>("");
  const [medicationPagination, setMedicationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: medicinesList, isFetching: isMedicationFetching } =
    useGetCataLogCode({
      gridSorting: [],
      pagination: medicationPagination,
      searchKey: medicationSearch,
      type: "DRUG_CATALOG",
      archive: false,
    });

  const icdCodesOptions = useMemo(() => {
    const fetched =
      icdCodesRaw?.data?.content?.map((l) => ({
        value: l?.uuid,
        label: `${l?.code} - ${l?.description || ""}`,
      })) ?? [];
    const selected = [
      ...((values.medicalCodes ?? []) as { [k: string]: string }[]),
      ...((icdCodes ?? []) as { [k: string]: string }[]),
    ];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s, i) =>
        !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey]) &&
        selected.findIndex((x) => x[idKey] === s[idKey]) === i
    );
    return [...missing, ...fetched];
  }, [icdCodesRaw?.data?.content, values.medicalCodes, icdCodes]);

  const allPharmaciesOptions = useMemo(() => {
    return allPharmacies?.data?.content?.map((it) => ({
      value: it?.uuid,
      label: it?.name,
    }));
  }, [allPharmacies?.data?.content]);

  const transformedMedicines = useMemo(() => {
    return (
      medicinesList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.drugName + " - " + (it?.description ?? ""),
      })) ?? []
    );
  }, [medicinesList]);

  return (
    <ScrollArea height={"calc(100vh - 170px)"} p={1}>
      <Box width={"100%"} height={"100%"}>
        {/* <NewMedicationDrawer disclosureHost={newMedDisc} /> */}
        <DrugCatalogDrawer
          dialogTitle={"Add Medication"}
          disclosureHost={newMedDisc}
        />

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <CustomSelect
              required
              readOnly={readOnly}
              label="Type"
              placeholder="Type"
              options={TYPE_OPTIONS}
              name="type"
              value={values.prescriptionFormType}
              onChange={(v) => setFieldValue("prescriptionFormType", v)}
              error={
                touched?.prescriptionFormType && errors?.prescriptionFormType
              }
            />
          </Grid>
          <Grid item xs={8} container spacing={2}>
            <Grid item xs={7.5}>
              <AsyncSelectScroll
                label="Medication Name"
                required
                disabled={readOnly}
                placeholder="Select Or Search Medicine"
                name="medicationId"
                handleBlur={handleBlur}
                onSearch={(v) => {
                  setMedicationSearch(v);
                  setMedicationPagination((p) => ({ ...p, page: 0 }));
                }}
                options={transformedMedicines}
                value={values?.medicationId}
                error={touched?.medicationId && errors?.medicationId}
                onChange={(v) => setFieldValue("medicationId", v)}
                pagination={medicationPagination}
                setPagination={setMedicationPagination}
                totalElements={medicinesList?.data?.page?.totalElements}
                loading={isMedicationFetching}
              />
            </Grid>
            <Grid item xs={4.5}>
              {!readOnly && (
                <StyledButtonV2
                  fullWidth
                  sx={{ height: INPUT_FIELD_HEIGHT }}
                  shouldAddIcon
                  onClick={() => newMedDisc.open()}
                  variant="outlined"
                  label="New Medication"
                />
              )}
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <ProviderSelectScroll
              disabled={readOnly}
              required
              label="Provider (Sign As)"
              name="provider"
              handleBlur={handleBlur}
              value={values?.provider ?? null}
              onChange={(v) => setFieldValue("provider", v)}
              prefillListOption={values?.provider ?? undefined}
              error={touched?.provider && errors?.provider}
            />
          </Grid>
          {/* <Grid item xs={4}>
            <CustomSelect
              readOnly={readOnly}
              label="Facility"
              options={FACILITY_OPTIONS}
              name="type"
              value={values?.facility}
              onChange={(v) => setFieldValue("facility", v)}
            />
          </Grid> */}
          <Grid item xs={4}>
            <CustomDatePicker
              required
              disabled={readOnly}
              maxDate={dayjs(new Date())}
              label="Prescription Date"
              placeholder="Choose Date"
              name="prescriptionDate"
              handleBlur={handleBlur}
              value={
                values.prescriptionDate
                  ? dayjs(values.prescriptionDate)
                  : undefined
              }
              onChange={(date) =>
                setFieldValue("prescriptionDate", date?.toISOString())
              }
              error={errors?.prescriptionDate as string | undefined}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <Text>INSTRUCTIONS</Text>
          </Grid>
          <Grid item xs={12}>
            <Input
              label="Directions to Patient"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "directionToPatient",
                value: values.directionToPatient ?? "",
                onChange: handleChange,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <NumberInput
              min={1}
              label="Duration"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "duration",
                value: values.duration ?? "",
                onChange: handleChange,
              }}
              error={touched?.duration && errors?.duration}
            />
          </Grid>
          <Grid item xs={4}>
            <NumberInput
              min={1}
              label="Quantity"
              required
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "quantity",
                value: values.quantity ?? "",
                onChange: handleChange,
              }}
              error={touched?.quantity && errors?.quantity}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomSelect
              readOnly={readOnly}
              required={!!values?.quantity}
              label="Unit"
              name="unit"
              handleBlur={handleBlur}
              placeholder="Select Unit"
              options={UNIT_OPTIONS}
              value={values?.unit}
              onChange={(v) => setFieldValue("unit", v)}
              error={touched?.unit && errors?.unit}
            />
          </Grid>
          <Grid item xs={4}>
            <NumberInput
              label="Refill"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Refill",
                name: "refills",
                value: values.refills ?? "",
                onChange: handleChange,
              }}
              error={touched?.refills && errors?.refills}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomDatePicker
              disabled={readOnly}
              required={!!values?.duration}
              label="Start Date"
              name="startDate"
              handleBlur={handleBlur}
              placeholder="Choose Date"
              value={values.startDate ? dayjs(values.startDate) : undefined}
              onChange={(date) =>
                setFieldValue("startDate", date?.toISOString())
              }
              error={touched?.startDate && errors?.startDate}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomDatePicker
              disabled={readOnly}
              required={!!values?.duration}
              minDate={dayjs(new Date())}
              label="End Date"
              name="endDate"
              handleBlur={handleBlur}
              placeholder="Choose Date"
              value={values.endDate ? dayjs(values.endDate) : undefined}
              onChange={(date) => setFieldValue("endDate", date?.toISOString())}
              error={errors?.endDate}
            />
          </Grid>{" "}
          <Grid item xs={4}>
            <CustomSelect
              readOnly={readOnly}
              required
              label="Substitutions or Dispense"
              name="substitutionsOrDispense"
              handleBlur={handleBlur}
              placeholder="Select Substitutions or Dispense"
              options={SUBSTITUTIONS_OR_DISPENSE_OPTIONS_LABELS}
              value={values?.substitutionsOrDispense}
              onChange={(v) => setFieldValue("substitutionsOrDispense", v)}
              error={
                touched?.substitutionsOrDispense &&
                errors?.substitutionsOrDispense
              }
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <Text>PHARMACY</Text>
          </Grid>
          <Grid item xs={12}>
            <AsyncSelectScroll
              disabled={readOnly}
              handleBlur={handleBlur}
              name="preferences"
              required
              label="Pharmacy"
              placeholder="Search and Select Pharmacy"
              options={allPharmaciesOptions}
              value={values?.preferences}
              onSearch={(v) => {
                setPharmacyKeys(v);
                setPharmacyPagination((p) => ({ ...p, page: 0 }));
              }}
              onChange={(v) => setFieldValue("preferences", v)}
              error={touched?.preferences && errors?.preferences}
              pagination={pharmacyPagination}
              setPagination={setPharmacyPagination}
              totalElements={allPharmacies?.data?.page?.totalElements}
              loading={isPharmacyFetching}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomDatePicker
              disabled={readOnly}
              label="Do not fill before"
              placeholder="Select Date"
              value={
                values.notFillBefore ? dayjs(values.notFillBefore) : undefined
              }
              onChange={(val) =>
                setFieldValue("notFillBefore", val?.toISOString())
              }
              error={errors?.notFillBefore}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomSelect
              readOnly={readOnly}
              label="Preferred Delivery"
              placeholder="Select Preferred Delivery Method"
              options={[{ value: "PRINT", label: "Print" }]}
              value={values.preferredDelivery}
              onChange={(v) => setFieldValue("preferredDelivery", v)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label="Prior Authorization"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter",
                name: "priorAuthorization",
                value: values.priorAuthorization ?? "",
                onChange: handleChange,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              label="Directions to Pharmacist"
              textFieldProps={{
                disabled: readOnly,
                placeholder: "Enter Directions for Pharmacist",
                name: "directionToPharmacist",
                value: values.directionToPharmacist ?? "",
                onChange: handleChange,
              }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <Text>DIAGNOSES</Text>
          </Grid>
          <Grid item xs={12}>
            <MaskMultiSelectScroll
              handleBlur={handleBlur}
              name="medicalCodes"
              required
              disabled={readOnly}
              label="Diagnosis (ICD-10)"
              placeholder="Search and Select Code"
              options={icdCodesOptions}
              onSearch={setIcdSearch}
              value={values.medicalCodes}
              onChange={(v) => setFieldValue("medicalCodes", v)}
              error={touched?.medicalCodes && errors?.medicalCodes}
              pagination={icdPagination}
              setPagination={setIcdPagination}
              totalElements={icdCodesRaw?.data?.page?.totalElements}
              loading={isIcdFetching}
            />
          </Grid>
        </Grid>
      </Box>
    </ScrollArea>
  );
};

export const UNIT_OPTIONS = [
  { label: "Percent", value: "PERCENT" },
  { label: "Capsule", value: "CAPSULE" },
  { label: "Drops", value: "DROPS" },
  { label: "g", value: "G" },
  { label: "Iu", value: "IU" },
  { label: "Mcg", value: "MCG" },
  { label: "Meq", value: "MEQ" },
  { label: "Mg", value: "MG" },
  { label: "MgPerKg", value: "MG_PER_KG" },
  { label: "MgPerMl", value: "MG_PER_ML" },
  { label: "Ml", value: "ML" },
  { label: "MlPerHour", value: "ML_PER_HOUR" },
  { label: "Puffs", value: "PUFFS" },
  { label: "Sprays", value: "SPRAYS" },
  { label: "Tablespoon", value: "TABLESPOON" },
  { label: "Tablets", value: "TABLETS" },
  { label: "Teaspoon", value: "TEASPOON" },
  { label: "Unit", value: "UNIT" },
];
